import {
  parseColor,
  resolveColor,
  formatTimestamp,
  eventImage,
  getHeaderImage,
  getBtvFallbackForBg,
} from './utils/utils';
import star1 from './assets/svgs/star1';
import star2 from './assets/svgs/star2';
import star3 from './assets/svgs/star3';
import star4 from './assets/svgs/star4';
import star5 from './assets/svgs/star5';
import star6 from './assets/svgs/star6';
import star7 from './assets/svgs/star7';
import star8 from './assets/svgs/star8';
import { EPaletteModes } from '@/types/theme-packs.types';
import type { TemplateProps } from './index';
import chroma from 'chroma-js';
import ContentEditable from './content-editable';
import { useImageUpload } from './utils/use-image-upload';
import styles from './template-styles.module.css';
import getRelativeContrastColor from '@/utils/colors/generate-relative-contrasting-color';

export const Blue = ({
  event,
  theme,
  mode,
  calendarLinks,
  customizations,
  href,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  startIntro,
  setStartIntro,
  setMainImage,
  setHeaderImage,
}: TemplateProps) => {
  const resolvedMode = mode ?? event.settings.design.color_theme ?? theme.theme_mode ?? EPaletteModes.Light;
  const baseBg = resolvedMode === EPaletteModes.Dark ? '#000000' : '#FFFFFF';
  const themeColors = theme.colors[resolvedMode] ?? theme.colors;
  const bg =
    customizations?.backgroundColor ||
    chroma
      .mix(
        baseBg,
        customizations?.bodyTextColor || parseColor(themeColors.bodyTextColor, { opacity: 0.5 }),
        0.05,
        'rgb'
      )
      .css();
  const container = resolveColor(themeColors.containerColor, customizations?.containerColor);
  const accent = resolveColor(themeColors.accentColor, customizations?.accentColor);
  const heading = getRelativeContrastColor(
    resolveColor(themeColors.headingTextColor, customizations?.headingTextColor),
    container
  );
  const body = getRelativeContrastColor(
    resolveColor(themeColors.bodyTextColor, customizations?.bodyTextColor),
    container
  );
  const buttonText = getRelativeContrastColor(
    resolveColor(themeColors.buttonTextColor ?? themeColors.bodyTextColor, customizations?.buttonTextColor),
    accent
  );
  const [loadingMain, uploadMain] = useImageUpload(setMainImage);
  const [loadingHeaderImg, uploadHeaderImg] = useImageUpload(setHeaderImage);
  const sessionDate = event.sessions[0]?.timestamp ? formatTimestamp(event.sessions[0].timestamp) : null;
  const images = [
    star1(accent),
    star2(accent),
    star3(accent),
    star4(accent),
    star5(accent),
    star6(accent),
    star7(accent),
    star8(accent),
  ];
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || getBtvFallbackForBg(accent);
  const bodyImg = customizations?.mainImage || eventImage(event);
  const headingFont = customizations?.headingFont
    ? `${customizations.headingFont}, sans-serif`
    : 'Arial Black, sans-serif';
  const bodyFont = customizations?.bodyFont ? `${customizations.bodyFont}, sans-serif` : 'Verdana, sans-serif';
  const _salutation = salutation ?? (customizations?.salutation || "You've been invited to:");
  const eventTitle = title ?? (customizations?.title || event.name);
  const eventDescription = description ?? customizations?.description;
  const _startIntro = startIntro ?? (customizations?.startIntro || 'The show starts on');
  const _cta = cta ?? (customizations?.cta || 'REGISTER');

  return (
    <table
      data-component="Blue"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--body': body,
          '--heading': heading,
          '--button-text': buttonText,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
          width: '100%',
        } as React.CSSProperties
      }
      width={520}
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '40px 40px 24px 40px' }}>
            <table
              width={520}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '520px', background: 'var(--container)', border: '1px solid var(--body)' }}
            >
              <tbody>
                {/* Decorative pattern row */}
                <tr>
                  <td>
                    <table
                      width="100%"
                      cellPadding={0}
                      cellSpacing={0}
                      border={0}
                      role="presentation"
                      style={{ borderBottom: '1px solid var(--body)' }}
                    >
                      <tbody>
                        <tr>
                          <td
                            align="center"
                            valign="middle"
                            height={48}
                            width={101}
                            style={{
                              background: 'var(--accent)',
                              borderRight: '1px solid var(--body)',
                              padding: '8px',
                            }}
                          >
                            {headerImg && (
                              <div
                                onClick={uploadHeaderImg}
                                data-cont="img"
                                className={loadingHeaderImg ? styles.imgLoading : undefined}
                              >
                                <img height="32px" src={headerImg} alt="" />
                              </div>
                            )}
                          </td>
                          {images.map((svg, i) => (
                            <td key={i} align="center" valign="middle" style={{ width: '36px', height: '36px' }}>
                              <span dangerouslySetInnerHTML={{ __html: svg }} />
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>

                {/* Title row */}
                <tr>
                  <td
                    style={{ padding: '8px 8px 24px 8px', textAlign: 'right', borderBottom: '1px solid var(--body)' }}
                  >
                    <ContentEditable
                      value={_salutation}
                      setValue={setSalutation}
                      style={{
                        color: 'var(--body)',
                        fontSize: '16px',
                        fontFamily: 'var(--body-font)',
                        fontWeight: 600,
                        lineHeight: '12.8px',
                        wordBreak: 'break-word',
                        marginBottom: '8px',
                      }}
                    />
                    <ContentEditable
                      value={eventTitle}
                      setValue={setTitle}
                      style={{
                        color: 'var(--heading)',
                        fontSize: '64px',
                        fontFamily: 'var(--heading-font)',
                        fontWeight: 800,
                        lineHeight: '51.2px',
                        wordBreak: 'break-word',
                      }}
                    />
                  </td>
                </tr>

                {/* Image + brief info row */}
                <tr>
                  <td style={{ borderBottom: '1px solid var(--body)' }}>
                    <table width="100%" cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          <td width={313} valign="top" style={{ borderRight: '1px solid var(--body)' }}>
                            {bodyImg && (
                              <div
                                onClick={uploadMain}
                                data-cont="img"
                                className={loadingMain ? styles.imgLoading : undefined}
                              >
                                <img width={313} src={bodyImg} style={{ display: 'block' }} alt="" />
                              </div>
                            )}
                          </td>
                          {eventDescription && (
                            <td valign="top" style={{ padding: '16px', verticalAlign: 'top' }}>
                              <ContentEditable
                                value={eventDescription}
                                setValue={setDescription}
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '14px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 500,
                                  lineHeight: '14px',
                                  wordWrap: 'break-word',
                                  width: '104px',
                                }}
                              />
                            </td>
                          )}
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>

                {/* Date + register button row */}
                <tr>
                  <td style={{ padding: '24px' }}>
                    <table width="100%" cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          <td valign="middle" style={{ verticalAlign: 'middle' }}>
                            {sessionDate && (
                              <>
                                <ContentEditable
                                  value={_startIntro}
                                  setValue={setStartIntro}
                                  style={{
                                    color: 'var(--body)',
                                    fontSize: '12px',
                                    fontFamily: 'var(--body-font)',
                                    fontWeight: 500,
                                    lineHeight: '16px',
                                    letterSpacing: '0.24px',
                                    wordWrap: 'break-word',
                                  }}
                                />
                                <div
                                  style={{
                                    color: 'var(--body)',
                                    fontSize: '14px',
                                    fontFamily: 'var(--body-font)',
                                    fontWeight: 700,
                                    lineHeight: '20px',
                                    wordWrap: 'break-word',
                                  }}
                                >
                                  {sessionDate}
                                </div>
                              </>
                            )}
                          </td>
                          <td valign="middle" align="right" style={{ verticalAlign: 'middle' }}>
                            <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                              <tbody>
                                <tr>
                                  <td
                                    align="center"
                                    valign="middle"
                                    style={{ background: 'var(--accent)', width: '148px', height: '40px' }}
                                  >
                                    <a
                                      href={anchorHref}
                                      onClick={(e) => e.preventDefault()}
                                      style={{
                                        textDecoration: 'none',
                                        padding: '6px 0 6px 0',
                                        width: '148px',
                                        height: '100%',
                                        textAlign: 'center',
                                      }}
                                    >
                                      <ContentEditable
                                        value={_cta}
                                        setValue={setCta}
                                        style={{
                                          color: 'var(--button-text)',
                                          fontSize: '13px',
                                          fontFamily: 'var(--body-font)',
                                          fontWeight: 600,
                                          lineHeight: '16px',
                                          wordWrap: 'break-word',
                                        }}
                                      />
                                    </a>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Calendar links footer */}
            <table cellPadding={0} cellSpacing={0} border={0} role="presentation" style={{ marginTop: '40px' }}>
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.iCal}
                      >
                        iCal
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--body)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.outlook}
                      >
                        Outlook
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--body)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.google}
                      >
                        Google
                      </a>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
